import { useState, useMemo } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { api } from "@/lib/api"
import { getAuth } from "@/lib/auth"
import { PlusIcon, PencilIcon, Trash2Icon, CopyIcon, CheckIcon, SearchIcon } from "lucide-react"

// ─── Types ───────────────────────────────────────────────────────────────────

type UserRole = "admin" | "operator" | "fleet_manager" | "driver"

type WorkLine = "nacional" | "internacional" | "batea"

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:       "Nacional",
  internacional:  "Internacional",
  batea:          "Batea",
}

const ALL_WORK_LINES: WorkLine[] = ["nacional", "internacional", "batea"]

interface UserRow {
  id: string
  email: string
  firstName: string
  lastName: string
  role: UserRole
  isActive: boolean
  phone?: string | null
  workLines?: WorkLine[] | null
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  admin: "Administrador",
  operator: "Operador",
  fleet_manager: "Gestor de flota",
  driver: "Chofer",
}

const ROLE_BADGE: Record<UserRole, string> = {
  admin: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  operator: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  fleet_manager: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  driver: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
}

function CopyableId({ id, full }: { id: string; full?: boolean }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(id)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      onClick={copy}
      className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors group"
      title="Copiar ID"
    >
      {full ? id : `${id.slice(0, 8)}…`}
      {copied
        ? <CheckIcon className="size-2.5 text-emerald-500" />
        : <CopyIcon className="size-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </button>
  )
}

const EMPTY_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  password: "",
  role: "operator" as UserRole,
  phone: "",
  isActive: true,
  workLines: [] as WorkLine[],
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const queryClient = useQueryClient()
  const isSuperAdmin = getAuth()?.user.isSuperAdmin === true

  // Filter state
  const [search, setSearch] = useState("")
  const [filterRole, setFilterRole] = useState<UserRole | "all">("all")

  // UI state
  const [sheetOpen, setSheetOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<UserRow | null>(null)
  const [deletingUser, setDeletingUser] = useState<UserRow | null>(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [formError, setFormError] = useState<string | null>(null)

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await api.users.$get()
      if (!res.ok) throw new Error("Error loading users")
      return res.json() as Promise<UserRow[]>
    },
  })

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (data: typeof EMPTY_FORM) => {
      const res = await api.users.$post({ json: data })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear usuario")
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await api.users[":id"].$put({ param: { id }, json: data as never })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar usuario")
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.users[":id"].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar usuario")
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
      setDeleteOpen(false)
      setDeletingUser(null)
    },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────

  function closeSheet() {
    setSheetOpen(false)
    setEditingUser(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
  }

  function openCreate() {
    setEditingUser(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
    setSheetOpen(true)
  }

  function openEdit(user: UserRow) {
    setEditingUser(user)
    setForm({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: "",
      role: user.role,
      phone: user.phone ?? "",
      isActive: user.isActive,
      workLines: user.workLines ?? [],
    })
    setFormError(null)
    setSheetOpen(true)
  }

  function openDelete(user: UserRow) {
    setDeletingUser(user)
    setDeleteOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    if (editingUser) {
      const payload: Record<string, unknown> = {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        role: form.role,
        phone: form.phone || null,
        isActive: form.isActive,
        workLines: form.role === "admin" ? null : form.workLines,
      }
      if (form.password) payload.password = form.password
      updateMutation.mutate({ id: editingUser.id, data: payload })
    } else {
      createMutation.mutate(form)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return users.filter((u) => {
      const matchesSearch =
        !term ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term)
      const matchesRole = filterRole === "all" || u.role === filterRole
      return matchesSearch && matchesRole
    })
  }, [users, search, filterRole])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Usuarios" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <p className="text-sm text-muted-foreground max-w-lg">
            Gestión de usuarios del sistema. Los usuarios con rol{" "}
            <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">admin</span>{" "}
            {isSuperAdmin
              ? "pueden ser modificados por el superadmin; su eliminación sigue protegida."
              : "no pueden ser modificados ni eliminados."}
          </p>
          <Button onClick={openCreate} className="shrink-0 gap-2">
            <PlusIcon className="size-4" />
            Nuevo usuario
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-48 max-w-sm">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Buscar por nombre o email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={filterRole} onValueChange={(v) => setFilterRole(v as UserRole | "all")}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Todos los roles">
                {filterRole === "all" ? "Todos los roles" : ROLE_LABELS[filterRole as UserRole]}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="admin">Administrador</SelectItem>
              <SelectItem value="operator">Operador</SelectItem>
              <SelectItem value="fleet_manager">Gestor de flota</SelectItem>
              <SelectItem value="driver">Chofer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Líneas</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right pr-4">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    Cargando usuarios...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    {users.length === 0 ? "No hay usuarios registrados" : "No se encontraron usuarios con ese criterio"}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((user) => {
                  const isAdmin = user.role === "admin"
                  const canEdit = !isAdmin || isSuperAdmin
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="font-medium">{user.firstName} {user.lastName}</div>
                        <CopyableId id={user.id} />
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_BADGE[user.role]}`}>
                          {ROLE_LABELS[user.role]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.role === "admin" ? (
                            <span className="text-[10px] px-1.5 py-0 rounded-full border border-muted-foreground/30 text-muted-foreground">
                              Todas
                            </span>
                          ) : user.workLines && user.workLines.length > 0 ? (
                            user.workLines.map((wl) => (
                              <span key={wl} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground border">
                                {WORK_LINE_LABELS[wl]}
                              </span>
                            ))
                          ) : (
                            <span className="text-[10px] text-muted-foreground/50">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`text-xs ${user.isActive ? "text-green-700 border-green-300" : "text-muted-foreground"}`}
                        >
                          {user.isActive ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right pr-4">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            disabled={!canEdit}
                            onClick={() => openEdit(user)}
                            title={canEdit ? "Editar usuario" : "No se puede editar un administrador"}
                          >
                            <PencilIcon className="size-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            disabled={isAdmin}
                            onClick={() => openDelete(user)}
                            title={isAdmin ? "No se puede eliminar un administrador" : "Eliminar usuario"}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2Icon className="size-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Sheet: Crear / Editar ─────────────────────────────────────────── */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => { if (!open) closeSheet(); else setSheetOpen(true) }}
      >
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>
              {editingUser ? "Editar usuario" : "Nuevo usuario"}
            </SheetTitle>
            <SheetDescription>
              {editingUser
                ? "Modificá los datos del usuario. Dejá la contraseña vacía para no cambiarla."
                : "Completá los datos para crear un nuevo usuario en el sistema."}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-2 flex-1">
            {/* Nombre / Apellido */}
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="u-firstName">Nombre</Label>
                <Input
                  id="u-firstName"
                  value={form.firstName}
                  onChange={(e) => setForm(f => ({ ...f, firstName: e.target.value }))}
                  required
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="u-lastName">Apellido</Label>
                <Input
                  id="u-lastName"
                  value={form.lastName}
                  onChange={(e) => setForm(f => ({ ...f, lastName: e.target.value }))}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="grid gap-1.5">
              <Label htmlFor="u-email">Email</Label>
              <Input
                id="u-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                required
              />
            </div>

            {/* Contraseña */}
            <div className="grid gap-1.5">
              <Label htmlFor="u-password">
                {editingUser ? "Nueva contraseña" : "Contraseña"}
                {editingUser && (
                  <span className="ml-1 text-xs text-muted-foreground font-normal">(opcional)</span>
                )}
              </Label>
              <Input
                id="u-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                required={!editingUser}
                minLength={8}
                placeholder={editingUser ? "Dejar vacío para no cambiar" : "Mínimo 8 caracteres"}
              />
            </div>

            {/* Rol */}
            <div className="grid gap-1.5">
              <Label htmlFor="u-role">Rol</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm(f => ({ ...f, role: v as UserRole }))}
              >
                <SelectTrigger id="u-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="operator">Operador</SelectItem>
                  <SelectItem value="fleet_manager">Gestor de flota</SelectItem>
                  <SelectItem value="driver">Chofer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* UUID */}
            {editingUser && (
              <div className="grid gap-1.5">
                <Label className="text-xs text-muted-foreground">ID de usuario</Label>
                <CopyableId id={editingUser.id} full />
              </div>
            )}

            {/* Líneas de trabajo */}
            <div className="grid gap-1.5">
              <Label>Líneas de trabajo</Label>
              {form.role === "admin" ? (
                <p className="text-xs text-muted-foreground">
                  Los administradores tienen acceso a todas las líneas.
                </p>
              ) : form.role === "driver" ? (
                <p className="text-xs text-muted-foreground">
                  Los choferes no tienen líneas de trabajo asignadas.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {ALL_WORK_LINES.map((wl) => (
                    <label key={wl} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.workLines.includes(wl)}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          workLines: e.target.checked
                            ? [...f.workLines, wl]
                            : f.workLines.filter(w => w !== wl)
                        }))}
                        className="h-4 w-4 rounded border-input accent-foreground cursor-pointer"
                      />
                      <span className="text-sm">{WORK_LINE_LABELS[wl]}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Teléfono */}
            <div className="grid gap-1.5">
              <Label htmlFor="u-phone">
                Teléfono
                <span className="ml-1 text-xs text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Input
                id="u-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              />
            </div>

            {/* Activo */}
            <div className="flex items-center gap-2.5">
              <input
                id="u-isActive"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                className="h-4 w-4 rounded border-input accent-foreground cursor-pointer"
              />
              <Label htmlFor="u-isActive" className="cursor-pointer font-normal">
                Usuario activo
              </Label>
            </div>

            {/* Error */}
            {formError && (
              <p className="text-sm text-destructive rounded-md bg-destructive/10 px-3 py-2">
                {formError}
              </p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving
                  ? "Guardando..."
                  : editingUser ? "Guardar cambios" : "Crear usuario"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Dialog: Confirmar eliminación ────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar usuario</DialogTitle>
            <DialogDescription>
              ¿Confirmás que querés eliminar a{" "}
              <strong>
                {deletingUser?.firstName} {deletingUser?.lastName}
              </strong>
              ?{" "}
              Esta acción desactiva el usuario y no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button
                variant="outline"
                onClick={() => setDeletingUser(null)}
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deletingUser && deleteMutation.mutate(deletingUser.id)}
            >
              {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
